import { Component, OnInit, OnDestroy } from '@angular/core';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import { SelfService } from '../bpg-self-services/self-services.service.ts';
import { BpgEventsService } from '../bpg-events/bpg-events.service';
import { ConstantValues } from '../bpg-common/models/bpg-constants';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BpgCommonService } from '../bpg-common/services/bpg-common.service.js';

type SignatoryKey = 'execSponsor' | 'chair' | 'viceChair';

interface Signatory {
  key: SignatoryKey;
  label: string;
  data: string | null;
  date: Date | null;
  signed: boolean;
}

const SUCCESS_ALERT_DURATION = 5000;

const ERROR_ALERT_DURATION = 8000;

export function normalizeEmail(email: any): string {
  return String(email != null ? email : '').trim().toLowerCase();
}

@Component({
  selector: 'app-tnc',
  templateUrl: './tnc.component.html',
  styleUrls: ['./tnc.component.css']
})
export class TnCComponent implements OnInit, OnDestroy {

  constructor(
    private readonly userService: SelfService,
    private readonly bpgCommonService: BpgCommonService,
    private readonly bpgEventsService: BpgEventsService,
    private readonly spinner: Ng4LoadingSpinnerService
  ) { }

  loginData: any = JSON.parse(localStorage.getItem('loginData') || '{}');
  userId = String(this.loginData && this.loginData.userId != null ? this.loginData.userId : '');
  userEmail = normalizeEmail(this.loginData && this.loginData.emailId != null ? this.loginData.emailId : '');
  isSuperAdmin = false;

  chapterId: string | null = null;
  bpgId = '';
  siteId = '';
  bpgName: string | null = null;
  siteName: string | null = null;
  selectedChapter: any = null;
  chapterList: any;

  sponsorObj: any;
  chairObj: any;
  viceChairObj: any;
  sponsorName: string | null = null;
  chairName: string | null = null;
  viceChairName: string | null = null;
  chairUserId: string | null = null;
  viceChairUserId: string | null = null;
  leadershipList: any;

  isLoading = true;
  hasUnsavedChanges = false;
  isSubmitting = false;
  isSubmitted = false;
  anySignatureCollected = false;
  showConfirmDialog = false;
  submittedDate: Date | null = null;
  canSign = false;
  hasPolicyReadTMNA = false;
  hasPolicyReadTFS = false;
  hasPolicyReadTmmcEthics = false;
  hasPolicyReadTmmcPolicy = false;
  hasPolicyReadTmmcSolicitation = false;
  printedAt = '';

  alertSuccess = false;
  alertError = false;
  alertMessage = '';

  readonly signatoryTypes: SignatoryKey[] = ['execSponsor', 'chair', 'viceChair'];

  private readonly payloadKeyMap: Record<SignatoryKey, { sign: string; date: string }> = {
    execSponsor: { sign: 'executiveSponsorSigned', date: 'modifiedSponsorSignedDate' },
    chair: { sign: 'chairSigned', date: 'modifiedChairSignedDate' },
    viceChair: { sign: 'coChairSigned', date: 'modifiedCoChairSignedDate' }
  };

  userSignatoryTypes: SignatoryKey[] = [];
  userCanSignMap: Partial<Record<SignatoryKey, boolean>> = {};

  private destroy$ = new Subject<void>();

  signatories: Record<SignatoryKey, Signatory> = {
    execSponsor: { key: 'execSponsor', label: 'Executive Sponsor', data: null, date: null, signed: false },
    chair: { key: 'chair', label: 'Chair', data: null, date: null, signed: false },
    viceChair: { key: 'viceChair', label: 'Vice Chair or Co-Chair', data: null, date: null, signed: false },
  };

  ngOnInit(): void {
    const adminId = localStorage.getItem('adminId') || '';
    this.isSuperAdmin = adminId.split(',').some((roleId: string) => roleId.trim() === '1');
    this.spinner.show();
    this.loadChapterData();
    this.logUserAction();
  }

  logUserAction() {
    const data = {
      userId: this.userId,
      categoryDescription: "Acknowledgement Form",
    };
    this.bpgCommonService.logUserAction(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe((_data) => { });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadChapterData(): void {
    const userAndRoleIdDTO = {
      userId: this.loginData['userId'],
      roleId: localStorage.getItem('adminId')
    };

    this.bpgEventsService.getChapterDropDown(userAndRoleIdDTO).pipe(takeUntil(this.destroy$)).subscribe(
      (data: any) => {
        this.isLoading = false;
        if (!data || data.length === 0) {
          this.spinner.hide();
          this.showErrorAlert('No chapters found for your account.');
          return;
        }
        this.chapterList = data;
        for (const item of data) {
          item.chapterName = item.siteName + ' - ' + item.bpgName;
        }
        if (data.length === 1 && data[0]) {
          this.chapterId = data[0].chapterId;
          this.fetchChapterData(data[0].chapterId);
        } else {
          this.spinner.hide();
        }
      },
      (error: any) => {
        this.isLoading = false;
        this.spinner.hide();
        console.error('Error loading chapter dropdown:', error);
        this.showErrorAlert('Failed to load chapter data.');
      }
    );
  }

  fetchChapterData(chapterId: string): void {
    if (!chapterId) { return; }

    this.spinner.show();
    this.resetFormState();

    const landingDto = {
      filter: { chapterId },
      moduleId: 0,
      page: 0,
      presentRole: '',
      searchTerm: '',
      size: ConstantValues.pageLimit,
      sortDirection: 'ASC',
      sortProperty: '',
      uid: ''
    };

    this.userService.getFullChapterList(landingDto).pipe(takeUntil(this.destroy$)).subscribe(
      (data: any) => {
        if (data && data.content && data.content.length > 0) {
          this.populateChapterDetails(data.content[0]);
        } else {
          this.spinner.hide();
          this.showErrorAlert('No chapter details found.');
        }
      },
      (error: any) => {
        this.spinner.hide();
        console.error('Error loading chapter list:', error);
        this.showErrorAlert('Failed to load chapter details.');
      }
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private populateChapterDetails(chapter: any): void {
    this.selectedChapter = chapter;
    this.chapterId = chapter.chapterId;
    this.bpgId = chapter.bpgId;
    this.siteId = chapter.siteId;
    this.bpgName = chapter.bpgName || null;
    this.siteName = chapter.siteName || null;

    if (chapter.chaptersExecutiveSponsor && chapter.chaptersExecutiveSponsor.length > 0) {
      this.sponsorObj = chapter.chaptersExecutiveSponsor[0];
      this.sponsorName = this.sponsorObj.executiveSponsorName || null;
    }

    this.chairUserId = null;
    this.viceChairUserId = null;

    if (chapter.chaptersLeadership && chapter.chaptersLeadership.length > 0) {
      for (const leader of chapter.chaptersLeadership) {
        if (!leader) { continue; }
        switch (leader.positionName) {
          case ConstantValues.leadershipPosition.chair:
            this.chairObj = leader;
            this.chairUserId = leader.userId != null ? String(leader.userId) : null;
            break;
          case ConstantValues.leadershipPosition.viceChair:
            this.viceChairObj = leader;
            this.viceChairUserId = leader.userId != null ? String(leader.userId) : null;
            break;
          default:
            break;
        }
      }
    }

    this.fetchLeadershipNames();
  }

  private fetchLeadershipNames(): void {
    const landingDto = {
      filter: {
        siteId: this.siteId,
        bpgId: this.bpgId,
        leadershipPosition: 'EMPLOYEE'
      },
      moduleId: 0,
      page: 0,
      presentRole: '',
      searchTerm: '',
      size: 0,
      sortDirection: 'ASC',
      sortProperty: '',
      uid: ''
    };

    this.userService.getUserDropDown(landingDto).pipe(takeUntil(this.destroy$)).subscribe(
      (data: any) => {
        this.leadershipList = data || [];
        this.resolveLeadershipNames();
        this.spinner.hide();
      },
      (error: any) => {
        this.spinner.hide();
        console.error('Error loading leadership list:', error);
      }
    );
  }

  private resolveLeadershipNames(): void {
    this.chairName = this.resolveUserName(this.chairUserId, this.chairObj);
    this.viceChairName = this.resolveUserName(this.viceChairUserId, this.viceChairObj);

    for (const type of this.signatoryTypes) {
      this.userCanSignMap[type] = this.checkSignPermission(type);
    }
    this.canSign = this.signatoryTypes.some(t => this.userCanSignMap[t]);
    this.userSignatoryTypes = this.signatoryTypes.filter(t => this.userCanSignMap[t]);

    this.loadExistingSubmission();
  }

  private resolveUserName(userId: string | null, fallbackObj: any): string | null {
    if (!userId) { return null; }
    const user = this.leadershipList.find((m: any) => String(m.userId) === userId);
    if (user) {
      return user.userFirstName + ' ' + user.userLastName;
    }
    return (fallbackObj && fallbackObj.emailAddress) || null;
  }

  resetFormState(): void {
    this.selectedChapter = null;
    this.chapterId = null;
    this.bpgId = '';
    this.siteId = '';
    this.bpgName = null;
    this.siteName = null;
    this.sponsorName = null;
    this.chairUserId = null;
    this.viceChairUserId = null;
    this.chairName = null;
    this.viceChairName = null;
    this.sponsorObj = null;
    this.chairObj = null;
    this.viceChairObj = null;
    this.hasUnsavedChanges = false;
    this.alertSuccess = false;
    this.alertError = false;
    this.alertMessage = '';
    this.isSubmitting = false;
    this.isSubmitted = false;
    this.anySignatureCollected = false;
    this.showConfirmDialog = false;
    this.submittedDate = null;
    this.userCanSignMap = {};
    this.userSignatoryTypes = [];
    this.canSign = false;
    this.hasPolicyReadTMNA = false;
    this.hasPolicyReadTFS = false;
    this.hasPolicyReadTmmcEthics = false;
    this.hasPolicyReadTmmcPolicy = false;
    this.hasPolicyReadTmmcSolicitation = false;

    for (const type of this.signatoryTypes) {
      const sig = this.signatories[type];
      sig.data = null;
      sig.date = null;
      sig.signed = false;
    }
  }

  goBack(): void {
    if (this.hasUnsavedChanges) {
      this.showConfirmDialog = true;
      return;
    }
    window.history.back();
  }

  confirmGoBack(): void {
    this.showConfirmDialog = false;
    window.history.back();
  }

  cancelGoBack(): void {
    this.showConfirmDialog = false;
  }

  onSigInput(type: SignatoryKey, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const sig = this.signatories[type];
    sig.data = value || null;
    sig.date = value ? new Date() : null;
    this.hasUnsavedChanges = this.hasAnyUserSignature;
  }

  clearSignature(type: SignatoryKey): void {
    const sig = this.signatories[type];
    if (!sig || !this.userCanSignMap[type] || sig.signed) { return; }
    sig.data = null;
    sig.date = null;
    if (!this.hasAnyUserSignature) {
      this.hasUnsavedChanges = false;
    }
  }

  private checkSignPermission(type: SignatoryKey): boolean {
    switch (type) {
      case 'execSponsor': {
        const sponsorEmail = normalizeEmail(this.sponsorObj && this.sponsorObj.executiveSponsorEmailId);
        const loginEmail = normalizeEmail(this.userEmail);
        return !!(this.sponsorObj && sponsorEmail && loginEmail && sponsorEmail === loginEmail);
      }
      case 'chair':
        return !!(this.chairObj && String(this.chairObj.userId) === this.userId);
      case 'viceChair':
        return !!(this.viceChairObj && String(this.viceChairObj.userId) === this.userId);
      default:
        return false;
    }
  }

  isSignatureLocked(type: SignatoryKey): boolean {
    return !!(this.signatories[type] && this.signatories[type].signed);
  }

  getSignatoryName(type: SignatoryKey): string {
    switch (type) {
      case 'execSponsor': return this.sponsorName || 'Not assigned';
      case 'chair': return this.chairName || 'Not assigned';
      case 'viceChair': return this.viceChairName || 'Not assigned';
      default: return 'Unknown';
    }
  }

  getSignaturePlaceholder(type: SignatoryKey): string {
    if (!this.userCanSignMap[type]) {
      return '\u2014 Awaiting ' + this.getSignatoryName(type) + '\u2019s signature \u2014';
    }
    if (!this.isAnyPolicyRead()) {
      return 'Review a policy above to sign';
    }
    return 'Type your full name';
  }

  getSignedCount(): number {
    return this.signatoryTypes.filter(t => this.signatories[t].signed).length;
  }

  get isFormComplete(): boolean {
    return this.signatoryTypes.every(t => this.signatories[t].signed);
  }

  get userHasSigned(): boolean {
    return this.userSignatoryTypes.length > 0 &&
      this.userSignatoryTypes.every(t => this.signatories[t].signed);
  }

  getUserSignTooltip(): string {
    if (!this.chapterId) { return ''; }
    if (this.userSignatoryTypes.length === 0) {
      return 'You are not authorized to sign for any role on this chapter.';
    }
    const labels = this.userSignatoryTypes.map((t) => this.signatories[t].label).join(', ');
    return 'You can sign for: ' + labels;
  }

  get hasAnyUserSignature(): boolean {
    return this.userSignatoryTypes.some(t => !!this.signatories[t].data);
  }

  submitTnC(): void {
    if (this.isSubmitting) { return; }

    if (!this.isAnyPolicyRead()) {
      this.showErrorAlert('Please review at least one ERG Policy by clicking the links above before submitting.');
      return;
    }

    this.isSubmitting = true;

    if (!this.bpgName) {
      this.isSubmitting = false;
      this.showErrorAlert('Please ensure chapter information is loaded.');
      return;
    }

    const payload = this.buildAcceptPayload();
    if (!payload) {
      this.isSubmitting = false;
      this.showErrorAlert('Please enter your signature before submitting.');
      return;
    }

    this.submitAccept(payload);
  }

  private buildAcceptPayload(): any | null {
    const payload: any = { chapterId: this.chapterId };
    let hasSignature = false;

    for (const type of this.userSignatoryTypes) {
      const sig = this.signatories[type];
      if (sig && sig.data) {
        payload[this.payloadKeyMap[type].sign] = sig.data;
        payload[this.payloadKeyMap[type].date] = sig.date ? sig.date.toISOString() : new Date().toISOString();
        hasSignature = true;
      }
    }

    return hasSignature ? payload : null;
  }

  private submitAccept(payload: any): void {
    this.userService.saveErgTnc(payload).pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        for (const type of this.userSignatoryTypes) {
          if (this.signatories[type].data) {
            this.signatories[type].signed = true;
          }
        }
        this.hasUnsavedChanges = false;
        this.isSubmitting = false;
        this.isSubmitted = true;
        this.anySignatureCollected = true;
        this.submittedDate = new Date();
        this.showSuccessAlert('ERG Transition Acknowledgement Form submitted successfully!');
        this.loadExistingSubmission();
      },
      (error: any) => {
        this.isSubmitting = false;
        console.error('Error submitting acceptance:', error);
        this.showErrorAlert('Failed to submit. Please try again.');
      }
    );
  }

  private loadExistingSubmission(): void {
    if (!this.chapterId || !this.userId) { return; }

    const payload = { chapterId: this.chapterId };

    this.userService.getErgTnc(payload).pipe(takeUntil(this.destroy$)).subscribe(
      (next: any) => {
        const data = Array.isArray(next) ? next[0] : next;
        if (!data) { return; }

        let hasAccepted = false;

        for (const type of this.signatoryTypes) {
          const signKey = this.payloadKeyMap[type].sign;
          const dateKey = this.payloadKeyMap[type].date;
          const signValue = data[signKey];

          if (!signValue) { continue; }

          hasAccepted = true;
          this.signatories[type].data = signValue;
          this.signatories[type].date = data[dateKey]
            ? new Date(data[dateKey])
            : new Date();
          this.signatories[type].signed = true;
        }

        const latestSignatoryDate = this.getLatestSignatoryDate();
        let resolvedDate: Date | null = null;
        if (latestSignatoryDate) {
          resolvedDate = latestSignatoryDate;
        } else if (data.submittedDate) {
          resolvedDate = new Date(data.submittedDate);
        }

        if (hasAccepted) {
          this.anySignatureCollected = true;
          this.isSubmitted = this.userSignatoryTypes.some((t) => {
            return this.signatories[t].signed;
          });
          this.submittedDate = resolvedDate;
        }
      },
      (error: any) => {
        console.warn('No existing TnC submission found:', error);
      }
    );
  }

  dismissAlert(): void {
    this.alertSuccess = false;
    this.alertError = false;
    this.alertMessage = '';
  }

  showSuccessAlert(message: string): void {
    this.alertError = false;
    this.alertSuccess = true;
    this.alertMessage = message;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => { this.alertSuccess = false; }, SUCCESS_ALERT_DURATION);
  }

  showErrorAlert(message: string): void {
    this.alertSuccess = false;
    this.alertError = true;
    this.alertMessage = message;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => { this.alertError = false; }, ERROR_ALERT_DURATION);
  }

  get isTmmcSite(): boolean {
    return (this.siteName || '').trim().toUpperCase() === 'TMMC';
  }

  isAnyPolicyRead(): boolean {
    return this.hasPolicyReadTMNA || this.hasPolicyReadTFS
      || this.hasPolicyReadTmmcEthics || this.hasPolicyReadTmmcPolicy || this.hasPolicyReadTmmcSolicitation;
  }

  onPolicyLinkClicked(policyType: 'TMNA' | 'TFS' | 'tmmcEthics' | 'tmmcPolicy' | 'tmmcSolicitation'): void {
    switch (policyType) {
      case 'TMNA':
        this.hasPolicyReadTMNA = true;
        break;
      case 'TFS':
        this.hasPolicyReadTFS = true;
        break;
      case 'tmmcEthics':
        this.hasPolicyReadTmmcEthics = true;
        break;
      case 'tmmcPolicy':
        this.hasPolicyReadTmmcPolicy = true;
        break;
      case 'tmmcSolicitation':
        this.hasPolicyReadTmmcSolicitation = true;
        break;
    }
  }

  trackByValue(_index: number, val: string): string { return val; }

  printForm(): void {
    this.printedAt = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    setTimeout(() => { window.print(); this.printedAt = ''; }, 50);
  }

  private getLatestSignatoryType(): SignatoryKey | null {
    let latestType: SignatoryKey | null = null;
    let latestTime = 0;

    for (const type of this.signatoryTypes) {
      const sig = this.signatories[type];
      if (sig.signed && sig.date) {
        const time = sig.date.getTime();
        if (time > latestTime) {
          latestTime = time;
          latestType = type;
        }
      }
    }
    return latestType;
  }

  get latestSignatoryLabel(): string | null {
    const type = this.getLatestSignatoryType();
    return type ? this.signatories[type].label : null;
  }

  get printStatusLabel(): string {
    const count = this.getSignedCount();
    const total = this.signatoryTypes.length;
    if (this.isFormComplete) { return 'Complete (' + total + ' of ' + total + ' signed)'; }
    if (this.anySignatureCollected) { return 'Partially Signed (' + count + ' of ' + total + ')'; }
    return 'No Signatures Yet';
  }

  getLatestSignatoryDate(): Date | null {
    const type = this.getLatestSignatoryType();
    return type ? this.signatories[type].date : null;
  }
}
